import { atom } from "jotai";

import type { Layout } from "@/shell/layout";

import { activeWorkspaceIdAtom, workspacesAtom } from "./atoms";

/* ============================================================ */
/* Undo/Redo history — pattern AE                                */
/*                                                                */
/* Stockage in-memory uniquement (pas de localStorage). Reset au */
/* reload de la page, comme AE. La persistence est sur le layout */
/* lui-meme via atomWithStorage(workspacesAtom).                 */
/*                                                                */
/* Per-workspace : chaque workspace a sa propre stack past/future*/
/* Switching de workspace ne perd pas l'historique des autres.   */
/*                                                                */
/* Throttle 250ms : les events rapides (drag-resize ~ 60Hz)      */
/* sont groupes en un seul undo step — seul le PREMIER push      */
/* capture le snapshot pre-action, les suivants dans la fenetre  */
/* throttle ne creent pas de nouvelle entree.                    */
/* ============================================================ */

interface HistoryState {
  past: Layout[];
  future: Layout[];
  lastPushAt: number;
}

const HISTORY_LIMIT = 100;
const HISTORY_THROTTLE_MS = 250;

/** Map workspaceId → { past, future, lastPushAt }. In-memory. */
export const historyByWorkspaceAtom = atom<Record<string, HistoryState>>({});

/**
 * Mutex pour skipper le tracking quand on EST en train d'undo/redo.
 * Sans ca, undo() qui set layoutAtom declencherait un push history,
 * brisant la logique past/future.
 */
export const skipHistoryAtom = atom(false);

/**
 * Push une snapshot du layout AVANT-mutation dans la stack past du
 * workspace actif. Appele depuis le writer de layoutAtom.
 *
 * Si on est dans la fenetre throttle (< 250ms du dernier push),
 * on ne push PAS — le snapshot pre-action est deja en stack.
 */
export function pushHistorySnapshot(
  history: Record<string, HistoryState>,
  workspaceId: string,
  preActionLayout: Layout
): Record<string, HistoryState> {
  const ws = history[workspaceId] ?? { past: [], future: [], lastPushAt: 0 };
  const now = Date.now();
  const inThrottle = now - ws.lastPushAt < HISTORY_THROTTLE_MS;

  if (inThrottle && ws.past.length > 0) {
    // Throttle : on ne push pas un nouveau snapshot, mais on bump le
    // timestamp et on clear le futur (toute mutation invalide la stack redo).
    return {
      ...history,
      [workspaceId]: { ...ws, future: [], lastPushAt: now },
    };
  }
  return {
    ...history,
    [workspaceId]: {
      past: [...ws.past, preActionLayout].slice(-HISTORY_LIMIT),
      future: [],
      lastPushAt: now,
    },
  };
}

/**
 * Undo : pop la derniere snapshot de past, push current dans future,
 * applique la snapshot. Set skipHistoryAtom pour eviter le tracking
 * du write layoutAtom qu'on declenche.
 */
export const undoAtom = atom(null, (get, set) => {
  const id = get(activeWorkspaceIdAtom);
  const history = get(historyByWorkspaceAtom);
  const ws = history[id];
  if (!ws || ws.past.length === 0) return;

  const previous = ws.past[ws.past.length - 1]!;
  const workspaces = get(workspacesAtom);
  const current = workspaces[id]?.layout;
  if (!current) return;

  // Apply previous layout WITHOUT triggering history push
  set(skipHistoryAtom, true);
  set(workspacesAtom, (w) => {
    const cur = w[id];
    if (!cur) return w;
    return { ...w, [id]: { ...cur, layout: previous, updatedAt: Date.now() } };
  });
  set(skipHistoryAtom, false);

  set(historyByWorkspaceAtom, (h) => ({
    ...h,
    [id]: {
      past: ws.past.slice(0, -1),
      future: [current, ...ws.future].slice(0, HISTORY_LIMIT),
      lastPushAt: 0, // reset throttle so next user action pushes
    },
  }));
});

/**
 * Redo : inverse de undo. Pop la premiere snapshot de future, push
 * current dans past, applique.
 */
export const redoAtom = atom(null, (get, set) => {
  const id = get(activeWorkspaceIdAtom);
  const history = get(historyByWorkspaceAtom);
  const ws = history[id];
  if (!ws || ws.future.length === 0) return;

  const next = ws.future[0]!;
  const workspaces = get(workspacesAtom);
  const current = workspaces[id]?.layout;
  if (!current) return;

  set(skipHistoryAtom, true);
  set(workspacesAtom, (w) => {
    const cur = w[id];
    if (!cur) return w;
    return { ...w, [id]: { ...cur, layout: next, updatedAt: Date.now() } };
  });
  set(skipHistoryAtom, false);

  set(historyByWorkspaceAtom, (h) => ({
    ...h,
    [id]: {
      past: [...ws.past, current].slice(-HISTORY_LIMIT),
      future: ws.future.slice(1),
      lastPushAt: 0,
    },
  }));
});

/** Etat undo/redo pour l'UI (boutons disabled, hint footer, etc.). */
export const canUndoAtom = atom((get) => {
  const id = get(activeWorkspaceIdAtom);
  return (get(historyByWorkspaceAtom)[id]?.past.length ?? 0) > 0;
});

export const canRedoAtom = atom((get) => {
  const id = get(activeWorkspaceIdAtom);
  return (get(historyByWorkspaceAtom)[id]?.future.length ?? 0) > 0;
});

export const historyDepthAtom = atom((get) => {
  const id = get(activeWorkspaceIdAtom);
  const ws = get(historyByWorkspaceAtom)[id];
  return {
    past: ws?.past.length ?? 0,
    future: ws?.future.length ?? 0,
  };
});

/**
 * Purge l'historique (style AE "Edit > Purge > Undo Cache"). N'affecte
 * que les stacks past/future ; le layout actuel et les workspaces
 * restent intacts.
 */
export const clearHistoryAtom = atom(null, (_get, set) => {
  set(historyByWorkspaceAtom, {});
});

/**
 * Reset complet du cockpit — destructive. Wipe localStorage des
 * workspaces + theme + active workspace + history. Le user revient a
 * l'etat initial (default workspace vide). Reload conseille apres.
 */
export const resetCockpitAtom = atom(null, (_get, set) => {
  // Clear localStorage entries
  if (typeof window !== "undefined") {
    const keys = Object.keys(window.localStorage);
    for (const k of keys) {
      if (k.startsWith("cockpit:")) {
        window.localStorage.removeItem(k);
      }
    }
  }
  set(historyByWorkspaceAtom, {});
});
